import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionLabel, PageHero, PrimaryCTA, ExpertCTA } from "@/components/ui-kit";
import { ServiceCard, FinalCTA, Icon } from "@/components/Sections";
import { SERVICES } from "@/data";
import { usePageMeta } from "@/hooks/usePageMeta";

const STAGES = [
  { icon: "Sprout", title: "Starting out", line: "Emergency fund, first SIP, term & health cover.", ids: ["mutual-funds", "insurance"] },
  { icon: "TrendingUp", title: "Growing", line: "Structured portfolio, tax efficiency, goal planning.", ids: ["investments", "tax-planning", "financial-planning"] },
  { icon: "Home", title: "Big milestones", line: "A home, a business — borrowing that fits the plan.", ids: ["loans", "fixed-income"] },
  { icon: "Gem", title: "Established", line: "Stable income, alternatives and legacy planning.", ids: ["fixed-income", "advanced"] },
];

export default function ServicesPage() {
  usePageMeta({
    title: "Services — Mutual Funds, Fixed Income, Insurance, Tax, Loans & Planning",
    description: "Explore Auxieron's financial services: mutual funds & SIPs, bonds, FDs & REITs, insurance, tax planning, loans via lending partners, financial planning and advanced investments.",
    path: "/services",
  });

  return (
    <>
      <PageHero
        label="Our Services"
        title="Every financial solution,"
        highlight="under one roof."
        lead="Eight connected services — so your investments, protection, borrowing and planning are never decided in isolation. Start anywhere; we'll help you see how it fits together."
        testId="services-page-label"
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <PrimaryCTA testId="services-primary-cta" />
          <ExpertCTA testId="services-expert-cta" />
        </div>
      </PageHero>

      {/* quick nav chips */}
      <section className="border-y border-navy/[0.07] bg-white py-6">
        <div className="mx-auto flex max-w-7xl flex-wrap gap-2 px-5 sm:px-8">
          {SERVICES.map((s) => (
            <Link
              key={s.id}
              to={`/services/${s.id}`}
              data-testid={`services-chip-${s.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-navy/10 bg-lightbg px-4 py-2 text-sm font-medium text-navy/80 transition-colors hover:border-royal hover:text-royal"
            >
              <Icon name={s.icon} className="h-4 w-4" /> {s.title}
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-lightbg py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* by life stage */}
      <section className="relative overflow-hidden bg-navy py-20 sm:py-28">
        <div className="grain absolute inset-0" />
        <div className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel dark testId="services-stage-label">Not sure where to start?</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Find your stage. We'll suggest the first step.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STAGES.map((st, i) => (
              <Reveal key={st.title} delay={i * 0.08}>
                <div data-testid={`stage-${i}`} className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-sm transition-colors hover:border-gold/40">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-gold">
                    <Icon name={st.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-bold text-white">{st.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{st.line}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {st.ids.map((id) => {
                      const s = SERVICES.find((x) => x.id === id);
                      return (
                        <Link key={id} to={`/services/${id}`} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-gold hover:text-navy">
                          {s.title} <ArrowRight className="h-3 w-3" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
